import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { ChevronDown, ChevronRight, Download, GitCompare, Loader2, Send, Trash2 } from 'lucide-react';
import type { ProposalNodeSet, RfqHubFileRelease, RfqPackage, RfqRequest } from '../../types';
import { db } from '../../utils/firebase';
import { packageHubReleaseIds } from '../../utils/rfqPackageReleases';
import { archivedReleaseTooltip, supersededStructureArchiveRows } from '../../utils/rfqPackageArchivedStructures';
import { archivePackageStructureIds, restorePackageStructureIds } from '../../utils/rfqPackageArchiveStructures';
import { deleteRfqHubFileRelease } from '../../utils/rfqHubFileReleaseService';
import { rfqRequestStageLabel } from '../../utils/rfqRequestStage';
import {
  groupTimberReleases,
  isLatestInLane,
  laneReleaseTitle,
  type RfqReleaseRound,
  type RfqStagedLane,
} from '../../utils/rfqReleaseGroups';
import {
  formatProposalLinkBadge,
  loadLinkedProposalNodeSets,
  proposalRfqLinkFromRelease,
} from '../../utils/proposalRfqVersionLink';
import {
  hasPrePortalTimberPackages,
  PRE_PORTAL_TIMBER_RELEASE_ID,
  timberReleaseVersionLabel,
} from '../../utils/timberRfqPrePortalVersion';
import { loadStagedReleaseNodeSets } from '../../utils/rfqHubTimberImport';
import { filterTargetsWithoutHubReleasePackage } from '../../utils/vendorPackageVersion';
import { parseTimberCsvForRfqHub, type CSVParseResult } from '../../utils/proposalCSVParser';
import TimberComparePickModal from '../proposals/TimberComparePickModal';
import '../proposals/TimberComparePickModal.css';
import { isReferenceRelease } from '../../utils/rfqReleasePurpose';
import RfqReleaseDisplayLabelEditor from './RfqReleaseDisplayLabelEditor';
import { RfqReleasePurposeBadge, RfqReleaseStructureChips } from './RfqReleaseScopeBadges';
import RfqVersionTimelineNode from './RfqVersionTimelineNode';
import './TimberRfqReleasePanel.css';

export interface TimberRfqReleasePanelProps {
  dealId: string;
  rfqProjectId?: string;
  publishTargetOrgIds: string[];
  releases: RfqHubFileRelease[];
  packages: RfqPackage[];
  selectedReleaseId: string;
  onSelectedReleaseChange: (releaseId: string) => void;
  /** Release ids that still have an approved quote (show Approved badge). */
  awardedReleaseIds?: Set<string>;
  /** Awarded suppliers — the only targets for reference releases (falls back to all targets). */
  referenceTargetOrgIds?: string[];
  publishBusy: boolean;
  canPublish: boolean;
  onPublishStaged?: (
    releaseId: string,
    preload: { nodeSets: ProposalNodeSet[]; sourceFileName: string },
  ) => void;
  /** Opens review modal before publishing. */
  onRequestPublishReview?: (
    release: RfqHubFileRelease,
    preload: { nodeSets: ProposalNodeSet[]; sourceFileName: string },
  ) => void;
  /** Request rounds sent from Files → RFQs (groups releases under their round). */
  requests?: RfqRequest[];
  /**
   * RFQ requests pilot on: sending moves to the Files wizard — per-release Send is
   * hidden and staged lanes offer "Request quotes in Files…" instead.
   */
  requestsEnabled?: boolean;
  /** Open Files → RFQs with these staged releases preselected in the request wizard. */
  onOpenFilesRequest?: (releaseIds: string[]) => void;
}

type ReleasePreload = {
  nodeSets: ProposalNodeSet[];
  sourceFileName: string;
  parsed: CSVParseResult;
};

/**
 * One rail node per *send*, not per structure: a request round is one card listing
 * the structures it covered, the staged-but-unsent structures are one batch card,
 * and only legacy (pre-rounds) sends still get a node each.
 */
type TimelineEntry =
  | { kind: 'section'; key: string; label: string; tone?: 'default' | 'staged' | 'legacy' }
  | { kind: 'round'; key: string; round: RfqReleaseRound }
  | { kind: 'batch'; key: string; lanes: RfqStagedLane[] }
  | { kind: 'release'; key: string; release: RfqHubFileRelease };

const fmtDueDate = (d?: Date): string => {
  if (!(d instanceof Date)) return '';
  try {
    return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(d);
  } catch {
    return d.toLocaleDateString();
  }
};

const fmtReleaseDate = (d?: Date): string => {
  if (!(d instanceof Date)) return '';
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(d);
  } catch {
    return d.toLocaleString();
  }
};

const TimberRfqReleasePanel: React.FC<TimberRfqReleasePanelProps> = ({
  dealId,
  rfqProjectId,
  publishTargetOrgIds,
  releases,
  packages,
  selectedReleaseId,
  onSelectedReleaseChange,
  awardedReleaseIds,
  referenceTargetOrgIds,
  publishBusy,
  canPublish,
  onPublishStaged,
  onRequestPublishReview,
  requests,
  requestsEnabled = false,
  onOpenFilesRequest,
}) => {
  const sortedReleases = useMemo(
    () => [...releases].sort((a, b) => b.releaseVersionIndex - a.releaseVersionIndex),
    [releases],
  );

  const groups = useMemo(
    () => groupTimberReleases({ releases: sortedReleases, packages, requests }),
    [sortedReleases, packages, requests],
  );

  /** Flat render order: rounds (newest first) → staged batch → earlier sends. */
  const timeline = useMemo<TimelineEntry[]>(() => {
    const out: TimelineEntry[] = [];
    if (groups.rounds.length) {
      out.push({ kind: 'section', key: 'sec-rounds', label: 'Requests' });
      for (const round of groups.rounds) out.push({ kind: 'round', key: `round-${round.request.id}`, round });
    }
    if (groups.staged.length) {
      out.push({
        kind: 'section',
        key: 'sec-staged',
        label: requestsEnabled ? 'Staged, not yet requested' : 'Staged, not yet sent',
        tone: 'staged',
      });
      out.push({ kind: 'batch', key: 'batch-staged', lanes: groups.staged });
    }
    if (groups.legacy.length) {
      out.push({ kind: 'section', key: 'sec-legacy', label: groups.rounds.length || groups.staged.length ? 'Earlier sends' : 'Sent releases', tone: 'legacy' });
      for (const r of groups.legacy) out.push({ kind: 'release', key: `rel-${r.id}`, release: r });
    }
    return out;
  }, [groups, requestsEnabled]);

  // Reference releases are never requested for quotes — leave them out of the wizard preselection.
  const stagedReleaseIds = useMemo(
    () =>
      groups.staged.flatMap(l =>
        l.releases
          .filter(r => !isReferenceRelease(r))
          .map(r => r.id)
          .filter((id): id is string => !!id),
      ),
    [groups],
  );
  const filesRequestAvailable = requestsEnabled && !!onOpenFilesRequest;

  // Round / batch cards are a mirror of what Files → RFQs sent: a one-line summary
  // ("3 structures: Sale Barn, Feed Barn, Equipment Barn") with the per-list rows
  // tucked behind "Show lists".
  const [expandedCards, setExpandedCards] = useState<Set<string>>(() => new Set());
  const [archiveBusyId, setArchiveBusyId] = useState<string | null>(null);
  const toggleCard = (key: string) =>
    setExpandedCards(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const renderListsToggle = (key: string, count: number, forcedOpen: boolean) => {
    const open = forcedOpen || expandedCards.has(key);
    return (
      <button
        type="button"
        className="srfq-timber-card-toggle"
        aria-expanded={open}
        onClick={() => toggleCard(key)}
        disabled={forcedOpen}
        title={forcedOpen ? 'Pick a list to send its quote request' : undefined}
      >
        {open ? <ChevronDown size={12} aria-hidden /> : <ChevronRight size={12} aria-hidden />}
        {open ? 'Hide lists' : `Show ${count === 1 ? 'list' : `${count} lists`}`}
      </button>
    );
  };

  const showPrePortalVersion = useMemo(() => hasPrePortalTimberPackages(packages), [packages]);

  const publishedReleaseIds = useMemo(() => {
    const ids = new Set<string>();
    packages.forEach((p) => {
      for (const id of packageHubReleaseIds(p)) ids.add(id);
    });
    return ids;
  }, [packages]);

  const preloadCache = useRef<Map<string, ReleasePreload>>(new Map());

  const [proposalNodeSets, setProposalNodeSets] = useState<ProposalNodeSet[] | null>(null);
  const [proposalLoading, setProposalLoading] = useState(true);
  const [proposalError, setProposalError] = useState<string | null>(null);

  const [publishPreload, setPublishPreload] = useState<ReleasePreload | null>(null);
  const [publishLoading, setPublishLoading] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);

  const [compareReleaseId, setCompareReleaseId] = useState<string | null>(null);
  const [comparePreload, setComparePreload] = useState<ReleasePreload | null>(null);
  const [compareBaselineNodeSets, setCompareBaselineNodeSets] = useState<ProposalNodeSet[] | null>(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareError, setCompareError] = useState<string | null>(null);
  const [removingReleaseId, setRemovingReleaseId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setProposalLoading(true);
    setProposalError(null);
    void (async () => {
      try {
        const snap = await getDoc(doc(db, 'proposalBuilders', dealId));
        if (cancelled) return;
        if (!snap.exists()) {
          setProposalNodeSets([]);
          return;
        }
        const data = snap.data() as { nodeSets?: ProposalNodeSet[] };
        setProposalNodeSets(data?.nodeSets ?? []);
      } catch (err) {
        if (!cancelled) {
          setProposalError(err instanceof Error ? err.message : 'Could not load proposal timber.');
          setProposalNodeSets([]);
        }
      } finally {
        if (!cancelled) setProposalLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dealId]);

  const loadReleasePreload = useCallback(async (release: RfqHubFileRelease): Promise<ReleasePreload> => {
    if (!release.id) {
      throw new Error('Release is unavailable.');
    }

    if (!release.releasedUrl) {
      throw new Error('Release file is unavailable.');
    }

    const { nodeSets, sourceFileName, trueDimensions } = await loadStagedReleaseNodeSets(release, dealId);
    const text = await fetch(release.releasedUrl).then((r) => {
      if (!r.ok) throw new Error('Failed to download released timber CSV.');
      return r.text();
    });
    const parsed = parseTimberCsvForRfqHub(text, { trueDimensions });
    const result: ReleasePreload = {
      nodeSets,
      sourceFileName,
      parsed,
    };
    preloadCache.current.set(release.id, result);
    return result;
  }, [dealId]);

  const selectedRelease = useMemo(
    () => sortedReleases.find((r) => r.id === selectedReleaseId) || null,
    [sortedReleases, selectedReleaseId],
  );

  const compareRelease = useMemo(
    () => (compareReleaseId ? sortedReleases.find((r) => r.id === compareReleaseId) || null : null),
    [sortedReleases, compareReleaseId],
  );

  useEffect(() => {
    const releaseId = selectedRelease?.id;
    if (!releaseId || !selectedRelease) {
      setPublishPreload(null);
      setPublishError(null);
      setPublishLoading(false);
      return;
    }
    const cached = preloadCache.current.get(releaseId);
    if (cached?.nodeSets) {
      setPublishPreload(cached);
      setPublishError(null);
      setPublishLoading(false);
      return;
    }
    let cancelled = false;
    setPublishLoading(true);
    setPublishError(null);
    // Node sets are enough to enable Send. The full CSV parse waits until Compare.
    void loadStagedReleaseNodeSets(selectedRelease, dealId)
      .then(({ nodeSets, sourceFileName }) => {
        if (cancelled) return;
        const result: ReleasePreload = {
          nodeSets,
          sourceFileName,
          parsed: {
            items: [],
            groups: [],
            csvType: 'materials',
            totalItems: 0,
            errors: [],
            debugInfo: { totalLines: 0, dataLines: 0, delimiter: ',', sampleRow: [] },
          },
        };
        preloadCache.current.set(releaseId, result);
        setPublishPreload(result);
      })
      .catch((err) => {
        if (!cancelled) {
          setPublishPreload(null);
          setPublishError(err instanceof Error ? err.message : 'Could not load released timber list.');
        }
      })
      .finally(() => {
        if (!cancelled) setPublishLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedRelease, dealId]);

  const openCompare = useCallback(
    (release: RfqHubFileRelease) => {
      if (!release.id) return;
      setCompareReleaseId(release.id);
      setComparePreload(null);
      setCompareBaselineNodeSets(null);
      setCompareError(null);
      setCompareLoading(true);
      void Promise.all([
        loadReleasePreload(release),
        loadLinkedProposalNodeSets(dealId, proposalRfqLinkFromRelease(release)),
      ])
        .then(([result, linkedNodeSets]) => {
          setComparePreload(result);
          setCompareBaselineNodeSets(linkedNodeSets);
        })
        .catch((err) => {
          setCompareError(err instanceof Error ? err.message : 'Could not load released timber list.');
        })
        .finally(() => {
          setCompareLoading(false);
        });
    },
    [loadReleasePreload, dealId],
  );

  const closeCompare = useCallback(() => {
    setCompareReleaseId(null);
    setComparePreload(null);
    setCompareBaselineNodeSets(null);
    setCompareError(null);
    setCompareLoading(false);
  }, []);

  const handleRemoveRelease = useCallback(
    async (release: RfqHubFileRelease) => {
      if (!release.id || publishedReleaseIds.has(release.id)) return;
      const versionLabel = timberReleaseVersionLabel(release.releaseVersionIndex);
      if (
        !window.confirm(
          `Remove staged release ${versionLabel}? The Project Hub file will stay; only this supplier staging copy is deleted.`,
        )
      ) {
        return;
      }
      setRemovingReleaseId(release.id);
      try {
        await deleteRfqHubFileRelease(release);
        preloadCache.current.delete(release.id);
        if (compareReleaseId === release.id) closeCompare();
      } catch (err) {
        alert(err instanceof Error ? err.message : 'Could not remove staged release.');
      } finally {
        setRemovingReleaseId(null);
      }
    },
    [publishedReleaseIds, compareReleaseId, closeCompare],
  );

  if (sortedReleases.length === 0 && !showPrePortalVersion) {
    return (
      <p className="srfq-timber-releases-empty">
        {filesRequestAvailable
          ? 'Nothing staged — request quotes from Project Hub → Files → RFQs.'
          : 'No staged releases yet — release a timber list from Project Hub → Files.'}
      </p>
    );
  }

  const showCompareModal =
    compareReleaseId
    && compareRelease
    && !compareLoading
    && !compareError
    && comparePreload
    && (compareBaselineNodeSets ?? proposalNodeSets);

  const compareProposalNodeSets = compareBaselineNodeSets ?? proposalNodeSets ?? [];
  const compareUsesLinkedSnapshot = !!compareRelease?.linkedProposalVersionId && !!compareBaselineNodeSets;

  const prePortalOnly = showPrePortalVersion && sortedReleases.length === 0;
  const nodeEntryIdx = timeline.map((e, i) => (e.kind === 'release' || e.kind === 'round' || e.kind === 'batch' ? i : -1)).filter(i => i >= 0);
  const firstNodeIdx = nodeEntryIdx[0] ?? -1;
  const lastNodeIdx = nodeEntryIdx[nodeEntryIdx.length - 1] ?? -1;
  const nodeItemClass = (idx: number) => {
    const isFirstNode = idx === firstNodeIdx;
    const isLastNode = !showPrePortalVersion && idx === lastNodeIdx;
    return `${isFirstNode ? ' srfq-timber-version-item--first-node' : ''}${
      isLastNode ? ' srfq-timber-version-item--last-node srfq-timber-version-item--last' : ''
    }`;
  };

  const renderFilesRequestButton = (ids: string[], label: string, small?: boolean) =>
    filesRequestAvailable && ids.length > 0 ? (
      <button
        type="button"
        className={small ? 'srfq-timber-lane-request-btn' : 'ops-new-btn srfq-timber-version-publish srfq-timber-files-request-btn'}
        onClick={() => onOpenFilesRequest?.(ids)}
        disabled={publishBusy}
        title="Opens the request wizard in Files → RFQs with these lists preselected."
      >
        <Send size={12} aria-hidden />
        {label}
      </button>
    ) : null;

  const renderSectionItem = (entry: Extract<TimelineEntry, { kind: 'section' }>) => (
    <li
      key={entry.key}
      className={`srfq-timber-version-item srfq-timber-version-item--section${
        entry.tone === 'legacy' ? ' srfq-timber-version-item--section-legacy' : ''
      }`}
    >
      <div className="srfq-timber-version-track" aria-hidden />
      <div className="srfq-timber-section-head">
        <p className="srfq-timber-version-group-label">{entry.label}</p>
      </div>
    </li>
  );

  /**
   * One structure's staged release. `compact` rows live inside a round / batch card
   * (flat, divider-separated); otherwise the row is its own card on the rail.
   */
  const renderReleaseRow = (release: RfqHubFileRelease, opts: { compact?: boolean } = {}) => {
    const isSelected = release.id === selectedReleaseId;
    const isPublished = release.id ? publishedReleaseIds.has(release.id) : false;
    const isReference = isReferenceRelease(release);
    const releaseTargets =
      isReference && referenceTargetOrgIds?.length ? referenceTargetOrgIds : publishTargetOrgIds;
    const pendingPublishTargets =
      release.id && rfqProjectId
        ? filterTargetsWithoutHubReleasePackage(packages, rfqProjectId, release.id, releaseTargets)
        : releaseTargets;
    const hasPendingPublish = pendingPublishTargets.length > 0;
    const quoteRequestsFullySent = isPublished && !hasPendingPublish;
    const latestInLane = isLatestInLane(release, sortedReleases);
    const archivedTooltip = archivedReleaseTooltip(release, packages);
    const proposalLinkBadge = formatProposalLinkBadge(proposalRfqLinkFromRelease(release));
    // Flag on: sending happens from the Files wizard; the per-release button goes away.
    // Reference releases are never requested through the wizard — always offer the share button.
    const showPublishButton = isSelected && (isReference || !filesRequestAvailable);
    const supplierNoun = (n: number) => `${n} supplier${n === 1 ? '' : 's'}`;
    const publishIdleLabel = isReference
      ? isPublished && hasPendingPublish
        ? `Share with ${supplierNoun(pendingPublishTargets.length)}`
        : 'Share Reference Release'
      : isPublished && hasPendingPublish
        ? `Send to ${supplierNoun(pendingPublishTargets.length)}`
        : 'Send Quote Requests';
    const publishSentLabel = isReference ? 'Shared with suppliers' : 'Quote requests sent';
    const publishTitle = quoteRequestsFullySent
      ? isReference
        ? 'This reference release was already shared with every awarded supplier.'
        : 'Quote request emails were already sent to all suppliers for this release.'
      : isPublished && hasPendingPublish
        ? isReference
          ? `Share this reference release with ${supplierNoun(pendingPublishTargets.length)} not yet on it. They will not be asked to requote.`
          : `Send quote requests to ${supplierNoun(pendingPublishTargets.length)} not yet on this release.`
        : isReference
          ? 'Suppliers receive the refined list to cut from. No quote is requested.'
          : undefined;
    return (
      <div
        key={release.id}
        className={`srfq-timber-version-row${isSelected ? ' srfq-timber-version-row--active' : ''}${
          opts.compact ? ' srfq-timber-version-row--compact' : ''
        }${!latestInLane ? ' srfq-timber-version-row--superseded' : ''}${
          isReference ? ' srfq-timber-version-row--reference' : ''
        }`}
      >
        <div
          className="srfq-timber-version-select"
          aria-pressed={isSelected}
          onClick={() => release.id && onSelectedReleaseChange(release.id)}
        >
          <span className="srfq-timber-version-main">
            <span className="srfq-timber-version-title">
              <span
                className="srfq-timber-version-lane-title"
                title={`Staged release ${timberReleaseVersionLabel(release.releaseVersionIndex)} on this work order`}
              >
                {laneReleaseTitle(release, sortedReleases)}
              </span>
              <RfqReleaseDisplayLabelEditor release={release} packages={packages} isPublished={isPublished} />
              {latestInLane && !opts.compact && <span className="srfq-timber-version-badge">Latest</span>}
              {!latestInLane && <span className="srfq-timber-version-badge">Superseded</span>}
              {archivedTooltip && (
                <span className="srfq-timber-version-badge srfq-timber-version-badge--archived" title={archivedTooltip}>
                  Archived
                </span>
              )}
              <RfqReleasePurposeBadge releasePurpose={release.releasePurpose} quoteSilent />
              {release.id && awardedReleaseIds?.has(release.id) && (
                <span className="srfq-timber-version-badge srfq-timber-version-badge--approved">Approved</span>
              )}
              {isPublished ? (
                <span className="srfq-timber-version-badge srfq-timber-version-badge--published">
                  {isReference ? 'Shared' : 'Published'}
                </span>
              ) : (
                <span className="srfq-timber-version-badge srfq-timber-version-badge--unpublished">
                  {isReference ? 'Not shared' : 'Unpublished'}
                </span>
              )}
            </span>
            <span className="srfq-timber-version-meta">
              {fmtReleaseDate(release.createdAt instanceof Date ? release.createdAt : undefined)}
              {release.releasedByName ? ` · ${release.releasedByName}` : ''}
              {proposalLinkBadge ? (
                <>
                  <br />
                  <span className="srfq-timber-version-proposal-link">{proposalLinkBadge}</span>
                </>
              ) : null}
              {isReference && (
                <span className="srfq-release-reference-note">
                  Refined list for the approved quote — suppliers cut from this, they do not requote it.
                </span>
              )}
            </span>
            <RfqReleaseStructureChips structureNames={release.structureNames} />
          </span>
        </div>
        <div className="srfq-timber-version-actions">
          <button
            type="button"
            className="srfq-timber-version-compare"
            onClick={() => openCompare(release)}
            disabled={proposalLoading}
            aria-label={`Compare ${timberReleaseVersionLabel(release.releaseVersionIndex)} to proposal`}
          >
            <GitCompare size={13} aria-hidden />
            Compare
          </button>
          <a
            className="srfq-timber-version-download"
            href={release.releasedUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Download ${release.fileName}`}
          >
            <Download size={13} aria-hidden />
          </a>
          {!isPublished && (
            <button
              type="button"
              className="srfq-timber-version-remove"
              onClick={() => void handleRemoveRelease(release)}
              disabled={removingReleaseId === release.id || publishBusy}
              aria-label={`Remove ${timberReleaseVersionLabel(release.releaseVersionIndex)}`}
              title="Remove staged release"
            >
              {removingReleaseId === release.id ? (
                <Loader2 size={13} className="srfq-timber-spin" aria-hidden />
              ) : (
                <Trash2 size={13} aria-hidden />
              )}
            </button>
          )}
          {showPublishButton && (
            <button
              type="button"
              className={`ops-new-btn srfq-timber-version-publish${
                quoteRequestsFullySent ? ' srfq-timber-version-publish--sent' : ''
              }`}
              disabled={
                publishBusy
                || quoteRequestsFullySent
                || !hasPendingPublish
                || !canPublish
                || !release.id
                || !publishPreload
                || publishLoading
                || !!publishError
              }
              title={publishTitle}
              onClick={() => {
                if (!release.id || !publishPreload || quoteRequestsFullySent || !hasPendingPublish) return;
                const payload = { nodeSets: publishPreload.nodeSets, sourceFileName: publishPreload.sourceFileName };
                if (onRequestPublishReview) onRequestPublishReview(release, payload);
                else onPublishStaged?.(release.id, payload);
              }}
            >
              {publishBusy
                ? 'Sending…'
                : publishLoading && !publishPreload
                  ? 'Preparing…'
                  : quoteRequestsFullySent
                    ? publishSentLabel
                    : publishIdleLabel}
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderRoundItem = (entry: Extract<TimelineEntry, { kind: 'round' }>, idx: number) => {
    const req = entry.round.request;
    const requestPackages = packages.filter((p) => p.rfqRequestId === req.id && p.id);
    const archivedOnRequest = (structureId: string) => {
      const owners = requestPackages.filter((p) =>
        (p.scopeStructures ?? []).some((s) => s.id === structureId),
      );
      return owners.length > 0 && owners.every((p) => (p.archivedStructureIds ?? []).includes(structureId));
    };
    const visibleLines = entry.round.lines.filter((line) => {
      const ids = (line.line.structures ?? []).map((s) => s.id).filter(Boolean);
      if (!ids.length) return true;
      return ids.some((id) => !archivedOnRequest(id));
    });
    const archivedLines = entry.round.lines.filter((line) => {
      const ids = (line.line.structures ?? []).map((s) => s.id).filter(Boolean);
      return ids.length > 0 && ids.every((id) => archivedOnRequest(id));
    });
    const dropRows = supersededStructureArchiveRows(requestPackages, packages);
    const n = visibleLines.length;
    const hasSelected = entry.round.lines.some(l => l.release?.id === selectedReleaseId);
    return (
      <li key={entry.key} className={`srfq-timber-version-item srfq-timber-version-item--card${nodeItemClass(idx)}`}>
        <div className="srfq-timber-version-track" aria-hidden>
          <RfqVersionTimelineNode selected={hasSelected} />
        </div>
        <div className={`srfq-timber-card srfq-timber-card--round${hasSelected ? ' srfq-timber-card--active' : ''}`}>
          <div
            className="srfq-timber-round-head srfq-timber-round-head--selectable"
            role="button"
            tabIndex={0}
            aria-pressed={hasSelected}
            title="Show this request's suppliers"
            onClick={() => {
              const first = entry.round.lines.find(l => l.release?.id)?.release?.id;
              if (first) onSelectedReleaseChange(first);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                const first = entry.round.lines.find(l => l.release?.id)?.release?.id;
                if (first) onSelectedReleaseChange(first);
              }
            }}
          >
            <span className="srfq-timber-round-title">
              <strong>{req.label}</strong>
              {rfqRequestStageLabel(req.stage) ? <span className="srfq-timber-version-badge srfq-timber-version-badge--stage">{rfqRequestStageLabel(req.stage)}</span> : null}
              {req.quoteDeadline ? (
                <span
                  className={`srfq-timber-version-badge srfq-timber-version-badge--due${
                    req.quoteDeadline.getTime() < Date.now() ? ' srfq-timber-version-badge--overdue' : ''
                  }`}
                >
                  Due {fmtDueDate(req.quoteDeadline)}
                </span>
              ) : null}
            </span>
            <span className="srfq-timber-version-meta">
              Sent {fmtReleaseDate(req.sentAt)}
              {req.sentByName ? ` · ${req.sentByName}` : ''}
              {` · ${req.supplierOrgIds.length} supplier${req.supplierOrgIds.length === 1 ? '' : 's'} · one RFQ per supplier`}
            </span>
            <span className="srfq-timber-card-structures">
              {n > 0 ? (
                <>
                  <strong>{n} structure{n === 1 ? '' : 's'}:</strong> {visibleLines.map(l => l.laneLabel).join(', ')}
                </>
              ) : (
                <strong>No structures still open on this request</strong>
              )}
              {archivedLines.length > 0 ? (
                <span className="srfq-timber-archived-note">
                  {' '}· archived: {archivedLines.map(l => l.laneLabel).join(', ')}
                </span>
              ) : null}
            </span>
            <span className="srfq-timber-card-foot" onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
              {dropRows.map((row) => (
                <button
                  key={row.id}
                  type="button"
                  className="srfq-timber-lane-request-btn"
                  disabled={archiveBusyId === row.id}
                  title="A later RFQ already asks for this structure. Drop it here so the supplier is not asked to quote it twice."
                  onClick={() => {
                    setArchiveBusyId(row.id);
                    void archivePackageStructureIds(row.packageIds, [row.id])
                      .catch((err) => {
                        console.error('[archive structures]', err);
                        window.alert('Could not archive those structures. Try again.');
                      })
                      .finally(() => setArchiveBusyId(null));
                  }}
                >
                  {archiveBusyId === row.id ? 'Removing…' : `Remove ${row.name}`}
                </button>
              ))}
              {archivedLines.map((line) => {
                const structureIds = (line.line.structures ?? []).map((s) => s.id).filter(Boolean);
                const busyKey = `restore:${line.key}`;
                return (
                  <button
                    key={busyKey}
                    type="button"
                    className="srfq-timber-lane-request-btn"
                    disabled={archiveBusyId === busyKey}
                    onClick={() => {
                      const packageIds = requestPackages
                        .filter((p) => (p.scopeStructures ?? []).some((s) => structureIds.includes(s.id)))
                        .map((p) => p.id!)
                        .filter(Boolean);
                      setArchiveBusyId(busyKey);
                      void restorePackageStructureIds(packageIds, structureIds)
                        .catch((err) => {
                          console.error('[restore structures]', err);
                          window.alert('Could not restore those structures. Try again.');
                        })
                        .finally(() => setArchiveBusyId(null));
                    }}
                  >
                    {archiveBusyId === busyKey ? 'Restoring…' : `Restore ${line.laneLabel}`}
                  </button>
                );
              })}
              {renderListsToggle(entry.key, entry.round.lines.length, false)}
            </span>
          </div>
          {expandedCards.has(entry.key) ? (
            <div className="srfq-timber-card-rows">
              {entry.round.lines.map(line =>
                line.release ? (
                  renderReleaseRow(line.release, { compact: true })
                ) : (
                  <p key={line.key} className="srfq-timber-version-meta srfq-timber-missing-release">
                    {line.laneLabel} — staged release removed
                  </p>
                ),
              )}
            </div>
          ) : null}
        </div>
      </li>
    );
  };

  const renderBatchItem = (entry: Extract<TimelineEntry, { kind: 'batch' }>, idx: number) => {
    const releases = entry.lanes.flatMap(l => l.releases);
    const hasSelected = releases.some(r => r.id === selectedReleaseId);
    const newest = releases.reduce<RfqHubFileRelease | null>(
      (acc, r) => (!acc || (r.createdAt instanceof Date && acc.createdAt instanceof Date && r.createdAt > acc.createdAt) ? r : acc),
      null,
    );
    const n = entry.lanes.length;
    return (
      <li key={entry.key} className={`srfq-timber-version-item srfq-timber-version-item--card${nodeItemClass(idx)}`}>
        <div className="srfq-timber-version-track" aria-hidden>
          <RfqVersionTimelineNode selected={hasSelected} />
        </div>
        <div className={`srfq-timber-card srfq-timber-card--batch${hasSelected ? ' srfq-timber-card--active' : ''}`}>
          <div className="srfq-timber-round-head">
            <span className="srfq-timber-round-title">
              <strong>{n === 1 ? entry.lanes[0].label : `${n} structures staged`}</strong>
              <span className="srfq-timber-version-badge srfq-timber-version-badge--unpublished">
                {filesRequestAvailable ? 'Not yet requested' : 'Not yet sent'}
              </span>
            </span>
            <span className="srfq-timber-version-meta">
              {newest?.createdAt instanceof Date ? `Last staged ${fmtReleaseDate(newest.createdAt)}` : 'Staged'}
              {newest?.releasedByName ? ` · ${newest.releasedByName}` : ''}
              {filesRequestAvailable
                ? ' · sent from Files → RFQs as one RFQ per supplier'
                : n > 1
                  ? ' · pick a structure to send its quote request'
                  : ''}
            </span>
            {n > 1 ? (
              <span className="srfq-timber-card-structures">{entry.lanes.map(l => l.label).join(', ')}</span>
            ) : null}
            <span className="srfq-timber-card-foot">
              {renderFilesRequestButton(stagedReleaseIds, n === 1 ? 'Request quotes in Files → RFQs…' : `Request all ${n} in Files → RFQs…`)}
              {renderListsToggle(entry.key, releases.length, !filesRequestAvailable)}
            </span>
          </div>
          {!filesRequestAvailable || expandedCards.has(entry.key) ? (
            <div className="srfq-timber-card-rows">
              {releases.map(r => renderReleaseRow(r, { compact: true }))}
            </div>
          ) : null}
        </div>
      </li>
    );
  };

  return (
    <>
      <div className="srfq-timber-releases">
        <ul className="srfq-timber-version-list srfq-timber-version-list--timeline" aria-label="Released timber lists">
          {timeline.map((entry, idx) => {
            if (entry.kind === 'section') return renderSectionItem(entry);
            if (entry.kind === 'round') return renderRoundItem(entry, idx);
            if (entry.kind === 'batch') return renderBatchItem(entry, idx);
            const { release } = entry;
            const isSelected = release.id === selectedReleaseId;
            return (
              <li
                key={entry.key}
                className={`srfq-timber-version-item${nodeItemClass(idx)}${
                  isLatestInLane(release, sortedReleases) ? '' : ' srfq-timber-version-item--inactive'
                }`}
              >
                <div className="srfq-timber-version-track" aria-hidden>
                  <RfqVersionTimelineNode selected={isSelected} />
                </div>
                {renderReleaseRow(release)}
              </li>
            );
          })}

          {showPrePortalVersion && (
            <>
              {(sortedReleases.length > 0 || prePortalOnly) && (
                <li
                  className={`srfq-timber-version-item srfq-timber-version-item--section${
                    sortedReleases.length > 0 ? ' srfq-timber-version-item--section-pre-portal' : ''
                  }`}
                >
                  <div className="srfq-timber-version-track" aria-hidden />
                  <div className="srfq-timber-section-head">
                    <p className="srfq-timber-version-group-label">Pre-portal imports</p>
                  </div>
                </li>
              )}
              <li
                className={`srfq-timber-version-item srfq-timber-version-item--last srfq-timber-version-item--last-node${
                  sortedReleases.length === 0 ? ' srfq-timber-version-item--first-node' : ''
                }${sortedReleases.length > 0 ? ' srfq-timber-version-item--inactive' : ''}`}
              >
                <div className="srfq-timber-version-track" aria-hidden>
                  <RfqVersionTimelineNode
                    selected={selectedReleaseId === PRE_PORTAL_TIMBER_RELEASE_ID}
                    prePortal
                  />
                </div>
                <div
                  className={`srfq-timber-version-row srfq-timber-version-row--pre-portal${
                    selectedReleaseId === PRE_PORTAL_TIMBER_RELEASE_ID
                      ? ' srfq-timber-version-row--active'
                      : ''
                  }`}
                >
                  <button
                    type="button"
                    className="srfq-timber-version-select"
                    onClick={() => onSelectedReleaseChange(PRE_PORTAL_TIMBER_RELEASE_ID)}
                    aria-pressed={selectedReleaseId === PRE_PORTAL_TIMBER_RELEASE_ID}
                  >
                    <span className="srfq-timber-version-main">
                      <span className="srfq-timber-version-title">
                        {timberReleaseVersionLabel(0)}
                        <span className="srfq-timber-version-label">Imported quotes</span>
                        {sortedReleases.length === 0 && (
                          <span className="srfq-timber-version-badge">Latest</span>
                        )}
                        {awardedReleaseIds?.has(PRE_PORTAL_TIMBER_RELEASE_ID) && (
                          <span className="srfq-timber-version-badge srfq-timber-version-badge--approved">
                            Approved
                          </span>
                        )}
                        <span className="srfq-timber-version-badge srfq-timber-version-badge--pre-portal">
                          Pre-portal
                        </span>
                      </span>
                      <span className="srfq-timber-version-meta">
                        Quotes received before the supplier portal existed
                      </span>
                    </span>
                  </button>
                </div>
              </li>
            </>
          )}
        </ul>

        {proposalError && (
          <p className="srfq-timber-compare-error" role="alert">{proposalError}</p>
        )}
        {!publishLoading && publishError && (
          <p className="srfq-timber-compare-error" role="alert">{publishError}</p>
        )}
      </div>

      {compareReleaseId && (
        <>
          {compareLoading && (
            <div className="srfq-timber-compare-overlay" role="dialog" aria-modal="true" aria-label="Loading comparison">
              <div className="srfq-timber-compare-overlay-card">
                <Loader2 size={20} className="srfq-timber-spin" aria-hidden />
                <span>Loading comparison…</span>
              </div>
            </div>
          )}
          {!compareLoading && compareError && (
            <div className="srfq-timber-compare-overlay" role="dialog" aria-modal="true" aria-label="Compare error">
              <div className="srfq-timber-compare-overlay-card srfq-timber-compare-overlay-card--error">
                <p>{compareError}</p>
                <button type="button" className="ops-new-btn" onClick={closeCompare}>
                  Close
                </button>
              </div>
            </div>
          )}
          {showCompareModal && (
            <TimberComparePickModal
              compareOnly
              readOnly
              swapPanels
              modalTitle={
                compareUsesLinkedSnapshot
                  ? `Compare v${compareRelease!.releaseVersionIndex} to linked proposal`
                  : `Compare v${compareRelease!.releaseVersionIndex} to proposal`
              }
              nodeSets={compareProposalNodeSets}
              parsed={comparePreload!.parsed}
              source={{
                fileName: compareRelease!.fileName,
                uploadedAt: compareRelease!.createdAt instanceof Date ? compareRelease!.createdAt : null,
                uploadedByName: compareRelease!.releasedByName,
              }}
              proposalPanelTitle={compareUsesLinkedSnapshot ? 'Linked proposal snapshot' : 'Proposal (live)'}
              csvPanelTitle="Released list"
              onCancel={closeCompare}
              onCreate={() => {}}
            />
          )}
        </>
      )}
    </>
  );
};

export default TimberRfqReleasePanel;
